import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
} from '@1trading/react';
import { Plus, X } from 'lucide-react';
import { useId, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ERROR_TOAST_DURATION_MS } from '@/hooks/queries/market-maker/use-market-maker';
import { getErrorMessage } from '@/lib/api/error';
import { type StartSimulationRequest, startSimulation } from '@/services/api/market-maker';
import type { TimelinePoint } from './simulation-math';
import { SimulationTimelineEditor } from './simulation-timeline-editor';
import { useMidPrice } from './use-mid-price';

const DEFAULT_START_DELAY_SECONDS = 0;
const DEFAULT_FOLLOWER_BETA = 1;

interface FollowerDraft {
  /** local row identity only (stable React key) — never serialised */
  id: number;
  market: string;
  beta: string;
}

/** Number fields coerce through Number.isFinite fallbacks (parseFloat so '' → fallback, not 0). */
const toFiniteNumber = (value: string, fallback: number): number => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export interface NewSimulationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** market ids for the Driver Market + follower selects (from the live markets query) */
  markets: string[];
  /** called after a successful POST — parent refetches simulations */
  onSubmitted: () => void;
}

/**
 * "Start Simulation" dialog.
 * Closing via Cancel/backdrop RETAINS all form state — operators iterate
 * on scenarios; only a successful submit resets. Keep this component mounted while
 * the console page lives so that retention actually holds.
 */
export const NewSimulationDialog = ({ open, onOpenChange, markets, onSubmitted }: NewSimulationDialogProps) => {
  const fieldId = useId();
  const [scenario, setScenario] = useState('');
  const [startDelay, setStartDelay] = useState(String(DEFAULT_START_DELAY_SECONDS));
  const [driverMarket, setDriverMarket] = useState('');
  const [timeline, setTimeline] = useState<TimelinePoint[]>([]);
  const [recoveryTimeline, setRecoveryTimeline] = useState<TimelinePoint[]>([]);
  const [followers, setFollowers] = useState<FollowerDraft[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const nextFollowerIdRef = useRef(0);

  // 5 s mid-price poll — only while the dialog is open with a market selected
  const currentPrice = useMidPrice(driverMarket, open);

  // Minimal validation improvement over the legacy dialog (which had none)
  const isFormValid = scenario.trim() !== '' && driverMarket !== '' && timeline.length >= 1;

  const resetForm = (): void => {
    setScenario('');
    setStartDelay(String(DEFAULT_START_DELAY_SECONDS));
    setDriverMarket('');
    setTimeline([]);
    setRecoveryTimeline([]);
    setFollowers([]);
  };

  const addFollower = (): void => {
    nextFollowerIdRef.current += 1;
    const draft: FollowerDraft = { id: nextFollowerIdRef.current, market: '', beta: String(DEFAULT_FOLLOWER_BETA) };
    setFollowers((previous) => [...previous, draft]);
  };
  const removeFollower = (id: number): void => setFollowers((previous) => previous.filter((follower) => follower.id !== id));
  const updateFollower = (id: number, patch: Partial<Pick<FollowerDraft, 'market' | 'beta'>>): void =>
    setFollowers((previous) => previous.map((follower) => (follower.id === id ? { ...follower, ...patch } : follower)));

  const handleSubmit = async (): Promise<void> => {
    if (!isFormValid || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const request: StartSimulationRequest = {
        scenario, // verbatim (validation guarantees non-blank, but no trimming)
        startDelay: toFiniteNumber(startDelay, DEFAULT_START_DELAY_SECONDS),
        driver: { market: driverMarket, timeline },
        // rows with an empty market are silently dropped at submit time
        followers: followers.filter((follower) => follower.market).map((follower) => ({ market: follower.market, beta: toFiniteNumber(follower.beta, DEFAULT_FOLLOWER_BETA) })),
        // recovery key OMITTED entirely when the series is empty
        ...(recoveryTimeline.length ? { recovery: { timeline: recoveryTimeline } } : {}),
      };
      await startSimulation(request);
      resetForm();
      onOpenChange(false);
      onSubmitted();
    } catch (error) {
      // Dialog stays open with all state intact
      toast.error(`Failed to start simulation: ${getErrorMessage(error)}`, { duration: ERROR_TOAST_DURATION_MS });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[900px]">
        <DialogHeader>
          <DialogTitle>Start Simulation</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${fieldId}-scenario`}>Scenario Name</Label>
              <Input id={`${fieldId}-scenario`} onChange={(event) => setScenario(event.target.value)} value={scenario} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${fieldId}-driver-market`}>Driver Market</Label>
              <Select onValueChange={setDriverMarket} value={driverMarket}>
                <SelectTrigger id={`${fieldId}-driver-market`}>
                  <SelectValue placeholder="Select market" />
                </SelectTrigger>
                <SelectContent>
                  {markets.map((market) => (
                    <SelectItem key={market} value={market}>
                      {market}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${fieldId}-start-delay`}>Start Delay (seconds)</Label>
              <Input id={`${fieldId}-start-delay`} inputMode="decimal" onChange={(event) => setStartDelay(event.target.value)} type="number" value={startDelay} />
            </div>
          </div>

          <section>
            <h3 className="mb-2 text-sm font-semibold">Price Timeline</h3>
            <SimulationTimelineEditor
              currentPrice={currentPrice}
              driver={timeline}
              onDriverChange={setTimeline}
              onRecoveryChange={setRecoveryTimeline}
              recovery={recoveryTimeline}
            />
          </section>

          <section>
            <h3 className="mb-2 text-sm font-semibold">Followers (optional)</h3>
            <div className="flex flex-col gap-2">
              {followers.map((follower) => (
                <div className="flex items-end gap-2" key={follower.id}>
                  <div className="flex flex-1 flex-col gap-1.5">
                    <Label htmlFor={`${fieldId}-follower-market-${follower.id}`}>Market</Label>
                    <Select onValueChange={(value) => updateFollower(follower.id, { market: value })} value={follower.market}>
                      <SelectTrigger id={`${fieldId}-follower-market-${follower.id}`}>
                        <SelectValue placeholder="Select market" />
                      </SelectTrigger>
                      <SelectContent>
                        {markets.map((market) => (
                          <SelectItem key={market} value={market}>
                            {market}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex w-28 flex-col gap-1.5">
                    <Label htmlFor={`${fieldId}-follower-beta-${follower.id}`}>Beta</Label>
                    <Input
                      id={`${fieldId}-follower-beta-${follower.id}`}
                      inputMode="decimal"
                      onChange={(event) => updateFollower(follower.id, { beta: event.target.value })}
                      type="number"
                      value={follower.beta}
                    />
                  </div>
                  <Button aria-label="Remove follower" onClick={() => removeFollower(follower.id)} size="icon" type="button" variant="ghost">
                    <X className="size-4" />
                  </Button>
                </div>
              ))}
              <div>
                <Button className="gap-1.5" onClick={addFollower} size="sm" type="button" variant="ghost">
                  <Plus className="size-4" />
                  Add Follower
                </Button>
              </div>
            </div>
          </section>
        </div>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} type="button" variant="outline">
            Cancel
          </Button>
          <Button className="gap-2" disabled={!isFormValid || isSubmitting} onClick={() => void handleSubmit()} type="button" variant="primary">
            {isSubmitting ? <Spinner className="size-4" /> : null}
            Start
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
NewSimulationDialog.displayName = 'NewSimulationDialog';
